import React from 'react';

import { CircularProgress, Paper, Stack, Typography } from '@mui/material';

import type { Nullable } from '@clearverify/types';

import type { EmployeeDataEmploymentRecordInfoFragment, EmployeeDataInfoFragment } from 'src/api';
import type { EmployeeInviteLinkArgs, ResendInviteScope } from 'src/hooks/employee';
import type { SxProp } from 'src/types/mui';

import { EmployeeDataEmploymentRecordCard } from '../EmployeeDataEmploymentRecordCard';

import { EmployeeDataInternalNotesCard } from './EmployeeDataInternalNotesCard';
import { EmployeeDataSummaryCard } from './EmployeeDataSummaryCard';

type EmployeeDataResultsSectionProps = {
  loading: boolean;
  result?: EmployeeDataInfoFragment;
  searched: boolean;
  /** Opened on one employee rather than searched for — changes the empty state. */
  directMode?: boolean;
  hideEmployerLiveIndicator: boolean;
  enableAdminControls?: boolean;
  inviteLinkArgs?: Nullable<EmployeeInviteLinkArgs>;
  employeeSsnEncrypted?: string;
  inviteEmailOptionsScope?: 'admin' | 'employerScope';
  sendInviteScope?: ResendInviteScope;
  onResendInvite?: (employeeId: string, inviteLinkArgs?: EmployeeInviteLinkArgs | null) => void;
  onInviteSent?: () => void;
  onDataDelete?: (employeeId: string) => void;
  onDataDeleteCancel?: (employeeId: string) => void;
  onOpenRecord?: (
    entry: EmployeeDataInfoFragment,
    record: EmployeeDataEmploymentRecordInfoFragment,
  ) => void;
};

export const EmployeeDataResultsSection: React.FC<EmployeeDataResultsSectionProps> = ({
  loading,
  result,
  searched,
  directMode,
  hideEmployerLiveIndicator,
  enableAdminControls,
  inviteLinkArgs,
  employeeSsnEncrypted,
  inviteEmailOptionsScope,
  sendInviteScope,
  onResendInvite,
  onInviteSent,
  onDataDelete,
  onDataDeleteCancel,
  onOpenRecord,
}) => {
  if (loading) {
    return (
      <Paper variant="outlined" sx={statePaperStyles}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Searching...
        </Typography>
      </Paper>
    );
  }

  if (!searched) {
    return (
      <Paper variant="outlined" sx={statePaperStyles}>
        <Typography variant="body1" color="text.secondary">
          Run a search to load employment records
        </Typography>
      </Paper>
    );
  }

  if (!result) {
    return (
      <Paper variant="outlined" sx={statePaperStyles}>
        <Typography variant="body1" color="text.secondary">
          {directMode
            ? // No search was run here, so don't report one as fruitless: the
              // employee may be outside this account's access, or their employer
              // may not be synced yet.
              'No data is available for this employee'
            : 'No employment records were found for the current search'}
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      <EmployeeDataSummaryCard
        entry={result}
        enableAdminControls={enableAdminControls}
        inviteLinkArgs={inviteLinkArgs}
        employeeSsnEncrypted={employeeSsnEncrypted}
        inviteEmailOptionsScope={inviteEmailOptionsScope}
        sendInviteScope={sendInviteScope}
        onResendInvite={onResendInvite}
        onInviteSent={onInviteSent}
        onDataDelete={onDataDelete}
        onDataDeleteCancel={onDataDeleteCancel}
      />

      {enableAdminControls && employeeSsnEncrypted && (
        <EmployeeDataInternalNotesCard
          key={employeeSsnEncrypted}
          entry={result}
          employeeSsnEncrypted={employeeSsnEncrypted}
        />
      )}

      {result.dataDeletedAt ? (
        <Paper variant="outlined" sx={statePaperStyles}>
          <Typography variant="body2" color="text.secondary">
            Employment and payroll data are not available because this user requested a data
            deletion.
          </Typography>
        </Paper>
      ) : result.employmentRecords.length === 0 ? (
        <Paper variant="outlined" sx={statePaperStyles}>
          <Typography variant="body2" color="text.secondary">
            No employment records available for this person yet
          </Typography>
        </Paper>
      ) : (
        result.employmentRecords.map(record => (
          <EmployeeDataEmploymentRecordCard
            key={record.companyId}
            employmentRecord={record}
            hideEmployerLiveIndicator={hideEmployerLiveIndicator}
            onSeeMore={onOpenRecord ? () => onOpenRecord(result, record) : undefined}
          />
        ))
      )}
    </Stack>
  );
};

const statePaperStyles: SxProp = {
  padding: 4,
  borderRadius: '10px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  borderStyle: 'dashed',
};
